import { capitalize, getFirstLetter } from '@wiseed/wikit';
import type { AddressModel } from '../models/api/AddressModel';
import {
	type AccountModel,
	type AccountStatus,
	type AccountStatusModel,
	adresseFiscaleEnum,
	UserRolesEnum,
	UserTrackingStatus
} from '../models/api/AccountModel';
import type { WalletModel } from '@app/models/api/WalletModel';
import { DocumentStatusEnum } from '@wiseed/wikit';
import { KYCStatusEnum } from '@app/enums/KYC';
import { isDateAfter, isDateBefore } from '@wiseed/wikit';
import { SignatureStatusEnum } from '@app/models/api/enums/SignatureStatusEnum';
import { urls } from '@app/urls';
import { AccountStateEnum } from '@wiseed/wikit';
import { getUrlKYC } from './kyc';

export const getShortName = (firstName: string, lastName: string) => {
	return `${capitalize(firstName)} ${capitalize(getFirstLetter(lastName))}.`;
};

export const getInitials = (firstName: string, lastName: string) => {
	return `${firstName.charAt(0)} ${lastName.charAt(0)}`;
};

export const isAddressEqual = (a1?: AddressModel, a2?: AddressModel) => {
	return (
		!!a1 &&
		!!a2 &&
		a1.address === a2.address &&
		a1.additionalAddress === a2.additionalAddress &&
		a1.city === a2.city &&
		a1.country === a2.country &&
		a1.postalCode === a2.postalCode
	);
};

export const isWalletOk = (user?: AccountModel) => {
	return !!user?.status?.walletOk;
};

export const isKycCompleteAndSignatureOK = (user: AccountModel) => {
	return (
		(user?.status?.kyc?.psfp?.statut === KYCStatusEnum.COMPLETE ||
			user?.status.kyc.psi.statut === KYCStatusEnum.COMPLETE) &&
		user?.signatureIdentityConfirmation?.status === SignatureStatusEnum.STATUT_OK
	);
};
export const isPSFPKycCompleteAndSignatureOK = (user: AccountModel) => {
	return (
		user?.status?.kyc?.psfp?.statut === KYCStatusEnum.COMPLETE &&
		user?.signatureIdentityConfirmation?.status === SignatureStatusEnum.STATUT_OK
	);
};
export const isPSIKycCompleteAndSignatureOK = (user: AccountModel) => {
	return (
		user?.status?.kyc?.psi?.statut === KYCStatusEnum.COMPLETE &&
		user?.signatureIdentityConfirmation?.status === SignatureStatusEnum.STATUT_OK
	);
};

export const isWalletPendingValidation = (user?: AccountModel) => {
	let isPending = false;
	const status = user?.status;
	if (status) {
		isPending =
			!status.walletOk && isKycCompleteAndSignatureOK(user) && isDocsPendingValidation(user);
	}
	return isPending;
};

export const isDocsPendingValidation = (user: AccountModel) => {
	const docs = user?.documents;
	return (
		(docs?.id1?.status === DocumentStatusEnum.SENT ||
			docs?.id1?.status === DocumentStatusEnum.PENDING) &&
		(docs?.dom?.status === DocumentStatusEnum.SENT ||
			docs?.dom?.status === DocumentStatusEnum.PENDING)
	);
};

export const hasToUploadDocs = (user: AccountModel): boolean => {
	const docs = user?.documents;
	for (const element in docs) {
		const docStatus = docs[element]?.status;
		if (docStatus === DocumentStatusEnum.INVALID || docStatus === DocumentStatusEnum.EXPIRED)
			return true;
	}
	return false;
};

export const hasToDoKYC = (user?: AccountModel): boolean => {
	return !isWalletOk(user) && !isWalletPendingValidation(user);
};

export const hasToUpdateProfile = (user: AccountModel): boolean => {
	const hasPreventSubscriptionDate = !!user?.status?.dateBlocageSouscription;
	const hasToReDoKyc = hasPSIExpired(user) || hasPSFPExpired(user);
	const hasPSIINCOMPLETE = hasToAnswerToOneProfileMatchingPsiSection(user) && user?.isPSIInvestor;
	const hasPSFPINCOMPLETE =
		hasToAnswerToOneProfileMatchingPsfpSection(user) && user?.isPSFPInvestor;
	return (
		isWalletOk(user) &&
		(hasPreventSubscriptionDate || hasToReDoKyc || hasPSIINCOMPLETE || hasPSFPINCOMPLETE)
	);
};

export const hasToUpdateFiscalAddress = (user?: AccountModel | null): boolean => {
	return (
		user?.status?.adresseFiscale === adresseFiscaleEnum.EXPIRED ||
		user?.status?.adresseFiscale === adresseFiscaleEnum.MISSING
	);
};

export const hasToUpdateFiscalAddressSoon = (user?: AccountModel | null): boolean => {
	return user?.status?.adresseFiscale === adresseFiscaleEnum.EXPIRES_SOON;
};

export const userCanMoneyOut = (user: AccountModel, wallet: WalletModel): boolean => {
	if (user && user?.isPSIInvestor && user?.isPSFPInvestor) {
		return (
			!hasToAnswerToOneProfileMatchingPsiSection(user) &&
			!hasToUpdateProfile(user) &&
			!hasToUpdateFiscalAddress(user) &&
			wallet?.moneyOut
		);
	} else if (user?.isPSIInvestor) {
		return (
			!hasToAnswerToOneProfileMatchingPsiSection(user) &&
			!hasToUpdateFiscalAddress(user) &&
			wallet?.moneyOut
		);
	} else if (user?.isPSFPInvestor) {
		return !hasToUpdateProfile(user) && !hasToUpdateFiscalAddress(user);
	} else {
		return false;
	}
};

export const canBypassIdentityPatchRestriction = (user: AccountModel): boolean => {
	// dans le cas de vieux comptes les champs ci-dessous n'étaient pas required
	// il faut donc que le user puisse les remplir même si son wallet est valide
	// (de base on ne peut pas modifier son identité si le wallet est validé)
	return (
		!user.identity.countyOfBirth ||
		!user.identity.countryOfBirth ||
		!user.identity.postalCodeOfBirth
	);
};

export const hasToUpdateIdDocNow = (user: AccountModel): boolean => {
	return user?.documents?.id1?.status === DocumentStatusEnum.EXPIRED;
};

export const hasToUpdateIdDocSoon = (user: AccountModel): boolean => {
	return user?.documents?.id1?.status === DocumentStatusEnum.EXPIRES_SOON;
};

export const isPersonalInfosOk = (status: AccountStatus): boolean => {
	return status.identiteOk && status.coordonneesOk && status.informationsPersonnellesOk;
};

export const hasToAnswerToOneProfileMatchingPsiSection = (user: AccountModel): boolean => {
	return user?.status?.kyc?.psi?.statut !== KYCStatusEnum.COMPLETE;
};
export const hasToAnswerToOneProfileMatchingPsfpSection = (user: AccountModel): boolean => {
	return (
		user?.status?.kyc?.psfp?.statut !== KYCStatusEnum.COMPLETE ||
		user?.status?.kyc?.psfp?.financialDataStatut !== KYCStatusEnum.COMPLETE
	);
};

export const hasPSIExpired = (user: AccountModel): boolean => {
	return user?.status?.kyc?.psi?.statut == KYCStatusEnum.EXPIRED && user?.isPSIInvestor;
};
export const hasPSFPExpired = (user: AccountModel): boolean => {
	return user?.status?.kyc?.psfp?.statut == KYCStatusEnum.EXPIRED && user?.isPSFPInvestor;
};

export const canHaveGhostAccess = (user: AccountModel): boolean => {
	return (
		user.roles?.includes(UserRolesEnum.ROLE_ADMIN) ||
		user.roles?.includes(UserRolesEnum.ROLE_SUPPORT)
	);
};

export const isDateCantSubscribePassed = (user: AccountModel): boolean => {
	return isDateAfter(new Date(), user.status?.dateBlocageSouscription);
};

export const getTrackingUserStatus = (user: AccountModel): UserTrackingStatus => {
	if (!isWalletOk(user)) {
		return UserTrackingStatus.N1;
	} else if (isWalletOk(user) && (user.isPSFPInvestor || user.isPSIInvestor)) {
		return UserTrackingStatus.N3;
	} else {
		return UserTrackingStatus.N2;
	}
};

export const getAccountStatus = (user: AccountModel, $t): AccountStatusModel => {
	const status: AccountStatusModel = {
		state: AccountStateEnum.UNDEFINED,
		iconName: undefined,
		title: undefined,
		ctaLink: undefined,
		ctaLabel: undefined
	};
	if (hasToDoKYC(user)) {
		status.title = $t('reminder.labels.profile');
		status.state = AccountStateEnum.ERROR;
		status.ctaLabel = $t('finishRegister');
		status.ctaLink = getUrlKYC();
	} else if (hasToUpdateProfile(user)) {
		if (hasToAnswerToOneProfileMatchingPsfpSection(user) && user?.isPSFPInvestor) {
			status.state = AccountStateEnum.ERROR;
			status.title = $t('reminder.labels.profile');
			status.ctaLabel = $t('updateProfile');
			status.ctaLink = urls.USER.ACCOUNT.MATCHING_PSFP;
		} else if (hasToAnswerToOneProfileMatchingPsiSection(user) && user?.isPSIInvestor) {
			status.state = AccountStateEnum.ERROR;
			status.title = $t('reminder.labels.profile');
			status.ctaLabel = $t('updateProfile');
			status.ctaLink = urls.USER.ACCOUNT.MATCHING_PSI;
		} else if (isDateAfter(new Date(), user?.status?.dateBlocageSouscription)) {
			status.state = AccountStateEnum.ERROR;
			status.title = $t('reminder.labels.profile');
			status.ctaLabel = $t('updateProfile');
			status.ctaLink = user?.status.informationsPersonnellesOk
				? urls.USER.ACCOUNT.MATCHING_PSFP
				: urls.USER.ACCOUNT.PROFILE;
		} else if (isDateBefore(new Date(), user?.status?.dateBlocageSouscription)) {
			status.state = AccountStateEnum.CHECKED;
			status.ctaLabel = $t('verifiedRegister');
		}
	} else if (hasToUpdateFiscalAddress(user) || hasToUpdateFiscalAddressSoon(user)) {
		status.state = AccountStateEnum.ERROR;
		status.title = $t('reminder.labels.profile');
		status.ctaLabel = $t('updateProfile');
		status.ctaLink = urls.USER.ACCOUNT.PROFILE;
	} else if (
		isWalletPendingValidation(user) ||
		(isDocsPendingValidation(user) && !hasToUploadDocs(user))
	) {
		status.state = AccountStateEnum.PENDING;
		status.ctaLabel = $t('pendingRegister');
	} else if (isWalletOk(user)) {
		if (hasToUpdateIdDocNow(user)) {
			status.title = $t('profile.myDocs');
			status.state = AccountStateEnum.ERROR;
			status.ctaLabel = $t('profile.hasToUpdateIdDocNow.ctaLabel');
			status.ctaLink = urls.USER.ACCOUNT.DOCUMENTS;
		} else if (hasToUpdateIdDocSoon(user)) {
			status.title = $t('profile.myDocs');
			status.state = AccountStateEnum.WARNING;
			status.ctaLabel = $t('profile.hasToUpdateIdDocSoon.ctaLabel');
			status.ctaLink = urls.USER.ACCOUNT.DOCUMENTS;
		} else if (hasToUploadDocs(user)) {
			status.title = $t('profile.myDocs');
			status.state = AccountStateEnum.ERROR;
			status.ctaLabel = $t('profile.hasToUpdateOtherDoc.ctaLabel');
			status.ctaLink = urls.USER.ACCOUNT.DOCUMENTS;
		} else {
			status.state = AccountStateEnum.CHECKED;
			status.ctaLabel = $t('verifiedRegister');
		}
	}

	status.iconName = 'account';
	return status;
};

export const getUserName = (user: AccountModel): string => {
	return `${user?.identity?.commonName ?? user?.identity?.lastName}`;
};
